import type { SagaIterator } from 'redux-saga';
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { ROUTES_ID } from '../routes/constants';
import { navigate } from '../routes/router';
import {
  buildOrderAccount,
  getPlaceOrderError,
  watchPaymentMethods,
} from '../shared/payment/paymentSagas';
import { createOrder } from '../shared/requestSagas';
import { setPlacing } from '../store/checkoutSlice';
import {
  setOrder,
  setPaymentError,
  setPaymentMethod,
} from '../store/paymentSlice';
import { selectRouteParams } from '../store/routingSelectors';
import type { OrderRequest } from '../types/payment';
import { onTrialConfirmPaymentMethod, onTrialPurchase } from '../utils/gtm';
import { getCookie } from '../utils/utils';
import { selectTrialView } from './trialSelectors';

const TRIAL_SEATS = 1;

export function* watchTrialPayment(): SagaIterator<void> {
  yield fork(watchPaymentMethods);
  yield takeEvery(setPaymentMethod, makeTrialOrder);
}

function* makeTrialOrder(): SagaIterator<void> {
  const { localization, session } = yield select((state) => state.app);
  const billing = yield select((state) => state.billing);
  const { paymentMethod, selectedMethod } = yield select(
    (state) => state.payment,
  );
  const { trialCatalog, sourceCatalog } = yield select((state) => state.trial);
  const view = yield select(selectTrialView);
  const params = yield select(selectRouteParams);

  if (!paymentMethod || !view || !localization) {
    return;
  }

  yield put(setPlacing(true));
  yield put(setPaymentError(null));

  onTrialConfirmPaymentMethod({
    trialCatalog,
    sourceCatalog,
    localization,
    session,
    email: billing.form.email,
    billingInfo: billing.info,
  });

  const referralUcc = getCookie('referral_ucc');

  const request: OrderRequest = {
    promoCode: '',
    paymentMethod,
    products: [
      {
        ratePlanId: view.ratePlan,
        seats: TRIAL_SEATS,
        migrationPathId: view.migrationPathId,
        sourceRatePlan: view.sourceRatePlan,
      },
    ],
    ...(referralUcc && { referralUcc }),
    account: buildOrderAccount(billing, localization, session),
  };

  const order = yield call(createOrder, request);

  if (order?.orderNumber) {
    yield put(setOrder(order));

    onTrialPurchase({
      trialCatalog,
      sourceCatalog,
      localization,
      session,
      email: billing.form.email,
      billingInfo: billing.info,
      transactionId: order.orderNumber,
      paymentType: selectedMethod,
    });

    yield put(navigate(ROUTES_ID.TRIAL_CONFIRMATION, { ...params }));
  } else {
    yield put(setPaymentError(getPlaceOrderError(order)));
  }

  yield put(setPlacing(false));
}
